import type React from "react";
import { useState } from "react";
import {
  ActionIcon,
  Box,
  Center,
  Group,
  Loader,
  Progress,
  Stack,
  Text,
  Tooltip,
} from "@mantine/core";
import { ConfirmModal, Table } from "@xplatform/design-system";
import {
  IconAlertTriangleFilled,
  IconCheck,
  IconCircleCheckFilled,
  IconClock,
  IconCopy,
  IconTrash,
} from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { JobStatus, type JobViewModel } from "@models/job";
import { useInfiniteScroll } from "@hooks/useInfiniteScroll";
import {
  JOB_ERROR_I18N_KEY,
  STATUS_COLOR,
  TERMINAL_STATUSES,
  formatTimestamp,
  isProcessingStatus,
  shortJobId,
} from "@utils/mappingServiceHelpers";

const statusIcon = (status: JobStatus): React.ReactNode => {
  if (isProcessingStatus(status)) {
    return <Loader size={10} color={STATUS_COLOR[status]} />;
  }
  if (status === JobStatus.Completed) return <IconCircleCheckFilled size={13} />;
  if (status === JobStatus.Failed) return <IconAlertTriangleFilled size={12} />;
  return <IconClock size={12} />;
};

interface JobsTableProps {
  jobs: JobViewModel[];
  currentUserId: string | null;
  onDeleteJob: (jobId: string) => Promise<boolean>;
  isLoadingMore: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  onOpenJob: (jobId: string) => void;
}

const JobsTable: React.FC<JobsTableProps> = ({
  jobs,
  currentUserId,
  onDeleteJob,
  isLoadingMore,
  hasMore,
  onLoadMore,
  onOpenJob,
}) => {
  const { t } = useTranslation();

  const [deleteTarget, setDeleteTarget] = useState<JobViewModel | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const copyId = (e: React.MouseEvent, jobId: string): void => {
    e.stopPropagation();
    void navigator.clipboard?.writeText(jobId);
    setCopiedId(jobId);
    window.setTimeout(() => setCopiedId(null), 2000);
  };

  const sentinelRef = useInfiniteScroll({
    hasMore,
    loading: isLoadingMore,
    onLoadMore,
  });

  const closeDeleteDialog = (): void => {
    if (isDeleting) return;
    setDeleteTarget(null);
  };

  const confirmDelete = async (): Promise<void> => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    const ok = await onDeleteJob(deleteTarget.jobId);
    setIsDeleting(false);
    if (ok) setDeleteTarget(null);
  };

  return (
    <Box className="mapping-service-page__table-wrap">
      <Table
        striped
        highlightOnHover
        withTableBorder
        withColumnBorders={false}
        verticalSpacing="sm"
        horizontalSpacing="md"
        className="mapping-service-page__table"
      >
        <Table.Thead>
          <Table.Tr>
            <Table.Th>{t("mappingServicePage.table.id")}</Table.Th>
            <Table.Th>{t("mappingServicePage.table.filename")}</Table.Th>
            <Table.Th>{t("mappingServicePage.table.status")}</Table.Th>
            <Table.Th>{t("mappingServicePage.table.progress")}</Table.Th>
            <Table.Th>{t("mappingServicePage.table.error")}</Table.Th>
            <Table.Th>{t("mappingServicePage.table.created")}</Table.Th>
            <Table.Th aria-label={t("mappingServicePage.table.actions")} />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {jobs.map((job) => {
            const isFailed = job.status === JobStatus.Failed;
            const isDisabled = isProcessingStatus(job.status) || isFailed;
            const isTerminal = TERMINAL_STATUSES.has(job.status);
            const hasCounts =
              job.rowsTotal !== null && job.rowsTotal !== undefined;
            const progressLabel = hasCounts
              ? t("mappingServicePage.main.rowsCount", {
                done: job.rowsDone ?? 0,
                total: job.rowsTotal,
                defaultValue: `${job.rowsDone ?? 0} / ${job.rowsTotal} rows`,
              })
              : isTerminal
                ? ""
                : t(`mappingServicePage.statuses.${job.status}`, {
                  defaultValue: job.status,
                }) + "...";
            const progressValue =
              job.rowsTotal && job.rowsTotal > 0
                ? Math.round(((job.rowsDone ?? 0) / job.rowsTotal) * 100)
                : null;
            const openAriaLabel = t("mappingServicePage.table.openRowAria", {
              filename: job.filename,
              defaultValue: `Open ${job.filename}`,
            });
            const errorFriendlyKey = job.error
              ? JOB_ERROR_I18N_KEY[job.error.errorCode]
              : undefined;
            const errorMessage = errorFriendlyKey
              ? t(`mappingServicePage.jobErrors.${errorFriendlyKey}`)
              : (job.error?.message ?? job.error?.description);
            const isOwner =
              !!currentUserId && job.owner?.userId === currentUserId;
            const canDelete = isOwner && isTerminal;

            let rowClassName = "mapping-service-page__row";
            if (isDisabled) rowClassName += " mapping-service-page__row--disabled";
            if (isFailed) rowClassName += " mapping-service-page__row--failed";

            return (
              <Table.Tr
                key={job.jobId}
                className={rowClassName}
                onClick={isDisabled ? undefined : () => onOpenJob(job.jobId)}
                onKeyDown={
                  isDisabled
                    ? undefined
                    : (e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onOpenJob(job.jobId);
                      }
                    }
                }
                tabIndex={isDisabled ? undefined : 0}
                role="button"
                aria-label={openAriaLabel}
                aria-disabled={isDisabled || undefined}
              >
                <Table.Td>
                  <Group gap={4} wrap="nowrap">
                    <Text size="sm" fw={600} ff="monospace">
                      {shortJobId(job.jobId)}
                    </Text>
                    <Tooltip
                      label={
                        copiedId === job.jobId
                          ? t("mappingServicePage.jobs.copied")
                          : t("mappingServicePage.jobs.copyId")
                      }
                      position="top"
                    >
                      <ActionIcon
                        size="xs"
                        variant="subtle"
                        radius="xl"
                        aria-label={t("mappingServicePage.jobs.copyId")}
                        onClick={(e) => copyId(e, job.jobId)}
                      >
                        {copiedId === job.jobId ? (
                          <IconCheck size={12} />
                        ) : (
                          <IconCopy size={12} />
                        )}
                      </ActionIcon>
                    </Tooltip>
                  </Group>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" fw={500} truncate style={{ minWidth: 0 }}>
                    {job.filename}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Group gap={6} wrap="nowrap" c={STATUS_COLOR[job.status]}>
                    {statusIcon(job.status)}
                    <Text size="sm" fw={500}>
                      {t(`mappingServicePage.statuses.${job.status}`, {
                        defaultValue: job.status,
                      })}
                    </Text>
                  </Group>
                </Table.Td>
                <Table.Td>
                  <Stack gap={2}>
                    <Text size="xs">{progressLabel}</Text>
                    {!isTerminal && (
                      <Progress
                        value={progressValue ?? 100}
                        size="xs"
                        animated={progressValue === null}
                      />
                    )}
                  </Stack>
                </Table.Td>
                <Table.Td>
                  {isFailed && errorMessage ? (
                    <Tooltip
                      label={errorMessage}
                      multiline
                      maw={360}
                      disabled={errorMessage.length <= 140}
                    >
                      <Group gap={6} wrap="nowrap" align="flex-start" c="red">
                        <IconAlertTriangleFilled
                          size={13}
                          style={{ flexShrink: 0, marginTop: 2 }}
                        />
                        <Text
                          size="xs"
                          lineClamp={4}
                          style={{ minWidth: 0, wordBreak: "break-word" }}
                        >
                          {errorMessage}
                        </Text>
                      </Group>
                    </Tooltip>
                  ) : isTerminal ? (
                    <Group gap={6} wrap="nowrap" c="dimmed">
                      <IconCircleCheckFilled
                        size={13}
                        style={{ flexShrink: 0, opacity: 0.45 }}
                      />
                      <Text size="xs">{t("mappingServicePage.table.noError")}</Text>
                    </Group>
                  ) : null}
                </Table.Td>
                <Table.Td>
                  <Text size="xs" c="dimmed">
                    {formatTimestamp(job.createdAt)}
                  </Text>
                </Table.Td>
                <Table.Td>
                  {canDelete && (
                    <Tooltip label={t("mappingServicePage.jobs.delete")} position="top">
                      <ActionIcon
                        size="sm"
                        variant="subtle"
                        color="red"
                        radius="xl"
                        aria-label={t("mappingServicePage.jobs.delete")}
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteTarget(job);
                        }}
                      >
                        <IconTrash size={14} />
                      </ActionIcon>
                    </Tooltip>
                  )}
                </Table.Td>
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>

      {hasMore && (
        <Center ref={sentinelRef} mih={48} mt="xs">
          {isLoadingMore && <Loader size="sm" />}
        </Center>
      )}

      <ConfirmModal
        opened={deleteTarget !== null}
        onClose={closeDeleteDialog}
        onConfirm={() => void confirmDelete()}
        size="sm"
        withCloseButton={!isDeleting}
        closeOnClickOutside={!isDeleting}
        closeOnEscape={!isDeleting}
        title={
          <Text fw={600}>{t("mappingServicePage.jobs.confirmDeleteTitle")}</Text>
        }
        overlayProps={{ backgroundOpacity: 0.5, blur: 2 }}
        message={t("mappingServicePage.jobs.confirmDeleteBody", {
          filename: deleteTarget?.filename ?? "",
        })}
        confirmLabel={t("mappingServicePage.jobs.delete")}
        cancelLabel={t("mappingServicePage.jobs.cancel")}
        danger
        loading={isDeleting}
      />
    </Box>
  );
};

export default JobsTable;
